import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from './client';

// Hotels in the API's own format (see /api/hotels in the backend README).

/** City and area suggestions: [{ key, name, fullName, country, hotelCount }]. Pass key as the search destination. */
export const searchDestinations = (query, signal) =>
  apiRequest(`/api/hotels/destinations?query=${encodeURIComponent(query)}`, { signal });

export const searchHotels = (request, signal) =>
  apiRequest('/api/hotels/search', { body: request, signal });

/** The next page of a search while its hasMore is true. */
export const nextHotelPage = (searchId, page, signal) =>
  apiRequest('/api/hotels/search/next', { body: { searchId, page }, signal });

/** Hotel content plus every live room option (optionId) for the searched stay. */
export const getHotelDetails = ({ searchId, supplierCode, hotelId }, signal) =>
  apiRequest('/api/hotels/details', { body: { searchId, supplierCode, hotelId }, signal });

/** Re-checks one option's live price. Returns a reviewId for checkout, valid for 15 minutes. */
export const reviewHotel = (optionId, signal) =>
  apiRequest('/api/hotels/review', { body: { optionId }, signal });

/** Guest or signed-in booking of a reviewed option; links it to an account by email like flight checkout. */
export const checkoutHotel = (request, token) =>
  apiRequest('/api/hotels/checkout', { body: request, token });

/** Live status, rooms, guests and cancellation penalties. Guests pass the booking email. */
export const getHotelBooking = (supplierBookingId, email, token, signal) =>
  apiRequest('/api/hotels/bookings/details', { body: { supplierBookingId, email: email || null }, token, signal });

/** Penalty and refund for cancelling now. Requires sign-in. */
export const quoteHotelCancellation = (supplierBookingId, token) =>
  apiRequest('/api/hotels/bookings/cancellation/quote', { body: { supplierBookingId }, token });

export const cancelHotel = (supplierBookingId, cancellationId, token) =>
  apiRequest('/api/hotels/bookings/cancellation', { body: { supplierBookingId, cancellationId }, token });

/**
 * The destination key to search: the one in the URL, or (popular links, older links) the best
 * suggestion for the name.
 */
async function destinationKey(destination, name, signal) {
  if (destination) return destination;
  const [best] = name ? await searchDestinations(name, signal) : [];
  if (!best) throw new Error(`We couldn't find "${name}". Choose the destination from the suggestions.`);
  return best.key;
}

/**
 * Searches whenever the search changes and loads further pages on request. `toRequest(search, key)`
 * builds the API request once the destination key is known.
 */
export function useHotelSearch(search, toRequest) {
  const key = search ? JSON.stringify(search) : '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: Boolean(search), loadingMore: false, data: null, hotels: [], error: '', moreError: '' });

  useEffect(() => {
    if (!key) return undefined;
    const s = JSON.parse(key);
    const controller = new AbortController();
    setState({ loading: true, loadingMore: false, data: null, hotels: [], error: '', moreError: '' });
    destinationKey(s.dest, s.name, controller.signal)
      .then((dest) => searchHotels(toRequest(s, dest), controller.signal))
      .then((data) => setState({ loading: false, loadingMore: false, data, hotels: data.hotels, error: '', moreError: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ loading: false, loadingMore: false, data: null, hotels: [], error: error.message || 'We could not load hotels for this search.', moreError: '' });
      });
    return () => controller.abort();
    // toRequest is a pure function of the search, which is already in the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const loadMore = useCallback(() => {
    const { data } = state;
    if (!data?.hasMore || state.loadingMore) return;
    setState((s) => ({ ...s, loadingMore: true, moreError: '' }));
    nextHotelPage(data.searchId, data.page + 1)
      .then((page) => setState((s) => {
        const seen = new Set(s.hotels.map((h) => `${h.supplierCode}:${h.hotelId}`));
        return {
          ...s,
          loadingMore: false,
          data: { ...s.data, page: page.page, hasMore: page.hasMore },
          hotels: [...s.hotels, ...page.hotels.filter((h) => !seen.has(`${h.supplierCode}:${h.hotelId}`))],
        };
      }))
      .catch((error) => setState((s) => ({ ...s, loadingMore: false, moreError: error.message || 'We could not load more hotels.' })));
  }, [state]);

  return { ...state, loadMore, retry: () => setAttempt((n) => n + 1) };
}
